import { useMemo, useState } from "react"
import { useQuery } from "urql"

import "../graphql/salesOpportunityQueries"

import { type Company, GetSalesOpportunityCustomerCompaniesDocument } from "@/graphql/codegen/graphql"
import { COMPANY_TYPE } from "@/screens/Companies/constants"

const PAGE_SIZE = 20

interface UseGetSalesOpportunityCustomerCompaniesParams {
  query?: string
}

interface UseGetSalesOpportunityCustomerCompaniesReturn {
  companies: Pick<Company, "companyId" | "name">[]
  isFetching: boolean
  loadMore: () => void
}

// TODO(BE-AK-5168): replace with a scoped "customers that have at least one in-scope quote"
// list once BE adds the endpoint. Today this returns the full active CUSTOMER directory.
export const useGetSalesOpportunityCustomerCompanies = ({
  query,
}: UseGetSalesOpportunityCustomerCompaniesParams): UseGetSalesOpportunityCustomerCompaniesReturn => {
  const [limit, setLimit] = useState(PAGE_SIZE)
  const [prevQuery, setPrevQuery] = useState(query)

  // Adjust state during render when `query` changes — the documented React replacement for a
  // state-sync useEffect (see React docs: "You Might Not Need an Effect").
  if (prevQuery !== query) {
    setPrevQuery(query)
    setLimit(PAGE_SIZE)
  }

  const [{ data, fetching }] = useQuery({
    query: GetSalesOpportunityCustomerCompaniesDocument,
    variables: {
      input: {
        filter: {
          types: [COMPANY_TYPE.CUSTOMER],
          ...(query && query.length > 1 && { query }),
        },
        limit,
        offset: 0,
      },
    },
  })

  const companies = useMemo<Pick<Company, "companyId" | "name">[]>(() => {
    if (data?.company?.list?.__typename === "CompanyListSuccess") {
      return data.company.list.companies
    }
    return []
  }, [data])

  const loadMore = () => setLimit((prev) => prev + PAGE_SIZE)

  return { companies, isFetching: fetching, loadMore }
}
